/**
 * stagedImportService.ts
 *
 * Staged Inventory Import & Export Pipeline.
 * Enforces file validation, dry-run schema diffs, error preview reports,
 * and formula injection sanitization on CSV exports.
 */

import type {
  ImportReport,
  ImportRow,
  ImportError,
  StockProjection,
} from './inventoryContracts.js';
import { InventoryMovementLedger } from './inventoryMovementLedger.js';
import type { AuthorizationContext } from '../../../kernel/authorization/capabilities.js';
import { requireCapability } from '../../../kernel/authorization/requireCapability.js';

/**
 * Formula Injection (CSV Injection) Sanitizer.
 * Neutralizes Excel / Google Sheets DDE command triggers.
 */
export function sanitizeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  // If string starts with =, +, -, @, tab (0x09), or carriage return (0x0D)
  if (/^[=+\-@\t\r]/.test(str)) {
    return `'${str}`; // Prepend single quote escape
  }
  return str;
}

export class StagedImportService {
  constructor(private readonly ledger: InventoryMovementLedger) {}

  /**
   * Dry-Run staged import parser and schema validator.
   */
  validateAndPreviewImport(
    rawContent: string,
    format: 'csv' | 'json',
    defaultLocationId: string = 'loc-main'
  ): ImportReport {
    const previewRows: ImportRow[] = [];
    const errors: ImportError[] = [];
    let warnings = 0;

    if (!rawContent || !rawContent.trim()) {
      return {
        accepted: 0,
        rejected: 0,
        warnings: 0,
        errors: [{ row: 0, code: 'E_EMPTY_FILE', message: 'Import file is empty' }],
        previewRows: [],
      };
    }

    if (format === 'json') {
      try {
        const parsed = JSON.parse(rawContent);
        const list = Array.isArray(parsed) ? parsed : [parsed];

        list.forEach((item, idx) => {
          const rowNum = idx + 1;
          const sku = String(item.sku || '').trim().toUpperCase();
          const productName = String(item.productName || sku).trim();
          const locationId = String(item.locationId || defaultLocationId).trim();
          const lotNumber = String(item.lotNumber || `LOT-IMP-${Date.now()}`).trim();
          const expiryDate = item.expiryDate ? String(item.expiryDate).trim() : undefined;
          const quantity = Number(item.quantity);

          if (!sku) {
            errors.push({ row: rowNum, field: 'sku', code: 'E_MISSING_SKU', message: 'Missing SKU' });
            return;
          }
          if (Number.isNaN(quantity) || quantity <= 0) {
            errors.push({
              row: rowNum,
              field: 'quantity',
              code: 'E_INVALID_QTY',
              message: `Quantity must be a positive integer (got ${item.quantity})`,
            });
            return;
          }
          if (expiryDate && !/^\d{4}-\d{2}-\d{2}$/.test(expiryDate)) {
            errors.push({
              row: rowNum,
              field: 'expiryDate',
              code: 'E_INVALID_DATE',
              message: 'Expiry date must be in YYYY-MM-DD format',
            });
            return;
          }

          previewRows.push({
            row: rowNum,
            sku,
            productName,
            locationId,
            lotNumber,
            expiryDate,
            quantity,
          });
        });
      } catch (err: any) {
        errors.push({ row: 0, code: 'E_JSON_PARSE', message: `Invalid JSON format: ${err.message}` });
      }
    } else {
      // CSV Parsing
      const lines = rawContent.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length <= 1) {
        errors.push({ row: 1, code: 'E_NO_DATA_ROWS', message: 'CSV has headers but no data rows' });
      } else {
        const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
        const skuIdx = header.indexOf('sku');
        const qtyIdx = header.indexOf('quantity');
        const nameIdx = header.indexOf('productname');
        const locIdx = header.indexOf('locationid');
        const lotIdx = header.indexOf('lotnumber');
        const expIdx = header.indexOf('expirydate');

        if (skuIdx === -1 || qtyIdx === -1) {
          errors.push({
            row: 1,
            code: 'E_MISSING_HEADERS',
            message: "CSV header must contain at least 'sku' and 'quantity' columns",
          });
        } else {
          for (let i = 1; i < lines.length; i++) {
            const rowNum = i + 1;
            const cols = lines[i].split(',').map((c) => c.trim());
            const sku = cols[skuIdx]?.toUpperCase() || '';
            const qtyStr = cols[qtyIdx] || '';
            const quantity = Number(qtyStr);
            const productName = (nameIdx !== -1 ? cols[nameIdx] : '') || sku;
            const locationId = (locIdx !== -1 ? cols[locIdx] : '') || defaultLocationId;
            const lotNumber = (lotIdx !== -1 ? cols[lotIdx] : '') || `LOT-IMP-${Date.now()}`;
            const expiryDate = expIdx !== -1 && cols[expIdx] ? cols[expIdx] : undefined;

            if (!sku) {
              errors.push({ row: rowNum, field: 'sku', code: 'E_MISSING_SKU', message: 'Row missing SKU' });
              continue;
            }
            if (Number.isNaN(quantity) || quantity <= 0) {
              errors.push({
                row: rowNum,
                field: 'quantity',
                code: 'E_INVALID_QTY',
                message: `Quantity must be positive integer (got ${qtyStr})`,
              });
              continue;
            }
            if (expiryDate && !/^\d{4}-\d{2}-\d{2}$/.test(expiryDate)) {
              errors.push({
                row: rowNum,
                field: 'expiryDate',
                code: 'E_INVALID_DATE',
                message: `Invalid expiry format for '${expiryDate}'. Expected YYYY-MM-DD`,
              });
              continue;
            }

            previewRows.push({
              row: rowNum,
              sku,
              productName,
              locationId,
              lotNumber,
              expiryDate,
              quantity,
            });
          }
        }
      }
    }

    return {
      accepted: previewRows.length,
      rejected: errors.length,
      warnings,
      errors,
      previewRows,
      previewToken: `token_dryrun_${Date.now()}`,
    };
  }

  /**
   * Commit dry-run verified rows into the live inventory movement ledger.
   */
  commitImport(
    context: AuthorizationContext,
    report: ImportReport,
    importBatchId: string = `imp_${Date.now()}`
  ): { committedCount: number } {
    requireCapability('inventory.import', context, { previewToken: report.previewToken });

    if (report.previewRows.length === 0) {
      throw new Error('[StagedImportService] Cannot commit empty import report');
    }

    const timestamp = new Date().toISOString();

    for (const row of report.previewRows) {
      if (row.productName) {
        this.ledger.setProductName(row.sku, row.productName);
      }

      this.ledger.appendMovement({
        movementId: `mov_imp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        sku: row.sku,
        locationId: row.locationId,
        lotNumber: row.lotNumber,
        expiryDate: row.expiryDate,
        quantityDelta: row.quantity,
        movementType: 'receipt',
        referenceId: importBatchId,
        performedBy: context.principal.id,
        reason: `Bulk Staged Import (${importBatchId})`,
        occurredAt: timestamp,
      });
    }

    return { committedCount: report.previewRows.length };
  }

  /**
   * Generate sanitized CSV Export string.
   */
  generateCsvExport(projections: readonly StockProjection[]): string {
    const headers = ['SKU', 'Product Name', 'Location ID', 'On Hand', 'Reserved', 'Available To Sell', 'Batches Count'];
    const rows: string[] = [headers.join(',')];

    for (const p of projections) {
      const line = [
        sanitizeCsvCell(p.sku),
        `"${sanitizeCsvCell(p.productName).replace(/"/g, '""')}"`,
        sanitizeCsvCell(p.locationId),
        String(p.onHand),
        String(p.reserved),
        String(p.availableToSell),
        String(p.batches.length),
      ];
      rows.push(line.join(','));
    }

    return rows.join('\n');
  }

  /**
   * Generate JSON Export string.
   */
  generateJsonExport(projections: readonly StockProjection[]): string {
    const records = projections.map((p) => ({
      sku: sanitizeCsvCell(p.sku),
      productName: sanitizeCsvCell(p.productName),
      locationId: p.locationId,
      onHand: p.onHand,
      reserved: p.reserved,
      availableToSell: p.availableToSell,
      batches: p.batches.map((b) => ({
        lotNumber: sanitizeCsvCell(b.lotNumber),
        expiryDate: b.expiryDate,
        onHand: b.onHand,
        availableToSell: b.availableToSell,
      })),
    }));

    return JSON.stringify(records, null, 2);
  }
}
